import { useState } from "react";
import {
  FcrCoreMonitorWindow,
  MONITOR_CORE_ID,
  moduleStreamsMonitor,
} from "../components/fcr/FcrCoreMonitorWindow";
import { CardFrame } from "./CardFrame";
import { useAuth } from "../context/AuthContext";
import { useFcrCoreModules } from "../hooks/useFcrCoreModules";
import { useSampleLog } from "../hooks/useSampleLog";
import { AllocationAdminCard } from "./AllocationAdminCard";
import { QuestDbViewCard } from "./QuestDbViewCard";
import { FoldCard } from "./FoldCard";
import { canControlFcrCore } from "../lib/access";
import { postSampleLogService } from "../lib/sampleLogApi";
import {
  postFcrCoreCmd,
  postFcrCoreService,
  type FcrCoreAction,
  type FcrCoreModule,
  type FcrCoreModuleGroup,
  type FcrCoreServiceAction,
} from "../lib/fcrCoreApi";

const GROUPS: { id: FcrCoreModuleGroup; title: string; blurb: string }[] = [
  {
    id: "init",
    title: "Init",
    blurb: "Roster files under /opt/fcr/config. Publish writes retained fcr/allocated from allocated.json.",
  },
  { id: "now", title: "Live", blurb: "Running pieces of the current scratch core." },
  {
    id: "standalone",
    title: "Stand-alone",
    blurb: "One-shot jobs. Start runs, then pauses so you can Start again.",
  },
  { id: "not_yet", title: "Not yet", blurb: "Empty slots. Start/pause/reset work; tick is a no-op." },
];

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page disabled:cursor-not-allowed disabled:opacity-40 disabled:text-text-tertiary";
const btnOn = "rounded-md border border-accent bg-accent px-2 py-1 text-white";

const ADMIN_ONLY_TITLE = "Admin only";

function MonitorToggle({
  on,
  onClick,
}: {
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={on ? btnOn : btn}
      aria-pressed={on}
      onClick={onClick}
    >
      Monitor
    </button>
  );
}

function stateClass(state: string) {
  if (state === "running") return "font-semibold text-status-ok";
  if (state === "stopped") return "font-medium text-status-error";
  return "font-medium text-text-secondary";
}

function kindLabel(kind: string) {
  if (kind === "mock") return "mock";
  if (kind === "stub") return "stub";
  if (kind === "init") return "init";
  return "live";
}

function formatDetail(mod: FcrCoreModule) {
  const d = mod.detail ?? {};
  if (mod.id === "load_balancing" && Array.isArray(d.gets)) {
    const elapsed = typeof d.elapsed_s === "number" ? `${d.elapsed_s}s` : "—";
    const n = (d.gets as { n?: number; path?: string }[])
      .map((g) => `${String(g.path || "").split("/").pop()}=${g.n ?? "?"}`)
      .join("  ");
    return `${elapsed}  ${n}`.trim();
  }
  if (mod.id === "occupancy" || mod.id === "session") {
    const desc =
      mod.hint ||
      (mod.id === "session"
        ? "Start / Stop → retained fcr/session/{id} + …/event"
        : "ocpp/sts Start / Stop / Status → c.ocpp.* / c.occupancy()");
    if (typeof d.free === "number") {
      return `${desc}  ·  free=${d.free}  paused=${d.paused}  charging=${d.charging}`;
    }
    return desc;
  }
  if (mod.id === "overall" && typeof d.kW === "number") {
    return `connected=${d.connected}  ${d.kW} kW`;
  }
  if (mod.id === "allocated") {
    const path = typeof d.path === "string" ? d.path : "/opt/fcr/config/allocated.json";
    const n = typeof d.n === "number" ? `n=${d.n}` : "n=—";
    if (d.published) {
      const count = typeof d.n === "number" ? d.n : "—";
      return `${path} → fcr/allocated · published=${count}`;
    }
    if (d.wrote) return `wrote ${n} from OCPP  ·  ${path}`;
    return `${path}  ·  ${n}${d.exists ? "" : "  (missing)"}  ·  Publish = fcr/allocated`;
  }
  if (mod.id === "bcu34_mvsi") {
    if (d.phase === "running" || d.phase === "stopping") {
      return `${d.phase}  ${d.done ?? 0}/${d.total ?? 0}  ok=${d.ok ?? 0}  fail=${d.fail ?? 0}  last=${d.last || "—"}`;
    }
    if (d.phase === "done") {
      const n = `${d.ok ?? 0} ok / ${d.fail ?? 0} fail of ${d.total ?? 0}`;
      const elapsed = typeof d.elapsed_s === "number" ? `  ${d.elapsed_s}s` : "";
      return `${n}${elapsed}  ·  MVSI=5s  Power,Current,Voltage,Current.Offered,Energy`;
    }
    return mod.hint || "Start to set BCU3/4 MVSI=5s and sampled measurands";
  }
  if (typeof d.note === "string") return d.note;
  if (mod.hint) return mod.hint;
  return "—";
}

function ModuleRow({
  mod,
  busyId,
  processRunning,
  monitorOn,
  canControl,
  onCmd,
  onToggleMonitor,
}: {
  mod: FcrCoreModule;
  busyId: string | null;
  processRunning: boolean;
  monitorOn: boolean;
  canControl: boolean;
  onCmd: (id: string, action: FcrCoreAction) => void;
  onToggleMonitor: (id: string) => void;
}) {
  const busy = busyId === mod.id;
  const locked = busy || !processRunning;
  const controlOff = !canControl;
  return (
    <tr className="border-b border-border-subtle last:border-b-0">
      <td className="py-2 pr-3 align-top">
        <div className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
          {mod.label}
        </div>
        <div className="font-mono text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
          {mod.id}
          {mod.file ? ` · ${mod.file}` : ""}
        </div>
      </td>
      <td className="py-2 pr-3 align-top">
        <span className="uppercase tracking-wide text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
          {kindLabel(mod.kind)}
        </span>
      </td>
      <td className="py-2 pr-3 align-top">
        <span className={stateClass(mod.state)} style={{ fontSize: "var(--font-body)" }}>
          {mod.state}
        </span>
      </td>
      <td className="max-w-md py-2 pr-3 align-top text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
        {mod.last_error ? (
          <span className="text-status-error">{mod.last_error}</span>
        ) : (
          formatDetail(mod)
        )}
      </td>
      <td className="py-2 align-top">
        <div className="flex flex-wrap gap-1" style={{ fontSize: "var(--font-micro)" }}>
          <button
            type="button"
            className={btn}
            disabled={controlOff || locked || mod.state === "running"}
            title={controlOff ? ADMIN_ONLY_TITLE : undefined}
            onClick={() => onCmd(mod.id, "start")}
          >
            {mod.id === "allocated" ? "Publish" : "Start"}
          </button>
          {mod.id === "allocated" && (
            <button
              type="button"
              className={btn}
              disabled={controlOff || locked}
              title={controlOff ? ADMIN_ONLY_TITLE : undefined}
              onClick={() => onCmd(mod.id, "from_ocpp")}
            >
              From OCPP
            </button>
          )}
          {mod.id !== "allocated" && (
            <button
              type="button"
              className={btn}
              disabled={controlOff || locked || mod.state !== "running"}
              title={controlOff ? ADMIN_ONLY_TITLE : undefined}
              onClick={() => onCmd(mod.id, "pause")}
            >
              {mod.id === "bcu34_mvsi" ? "Stop" : "Pause"}
            </button>
          )}
          <button
            type="button"
            className={btn}
            disabled={controlOff || locked}
            title={controlOff ? ADMIN_ONLY_TITLE : undefined}
            onClick={() => onCmd(mod.id, "reset")}
          >
            Reset
          </button>
          {moduleStreamsMonitor(mod) && (
            <MonitorToggle on={monitorOn} onClick={() => onToggleMonitor(mod.id)} />
          )}
        </div>
      </td>
    </tr>
  );
}

function SampleLogCard({ canControl }: { canControl: boolean }) {
  const { snapshot, error, reload } = useSampleLog();
  const [busy, setBusy] = useState(false);
  const [cmdError, setCmdError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const running = Boolean(snapshot?.running);

  async function onService(action: "start" | "stop") {
    setBusy(true);
    setCmdError(null);
    try {
      await postSampleLogService(action);
      await new Promise((r) => window.setTimeout(r, 800));
      await reload();
    } catch (err) {
      setCmdError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <FoldCard
      title="Charger sample log"
      open={open}
      onToggle={() => setOpen((value) => !value)}
      summaryClassName={running ? "font-semibold text-status-ok" : "font-medium text-text-secondary"}
      summary={
        <>
          {running ? "running" : "stopped"}
          {running && snapshot?.pid ? ` · pid ${snapshot.pid}` : ""}
          {snapshot?.block ? ` · block ${snapshot.block}` : ""}
          {typeof snapshot?.chargers === "number" ? ` · ${snapshot.chargers} chargers` : ""}
        </>
      }
      actions={
        <div className="flex flex-wrap gap-1" style={{ fontSize: "var(--font-micro)" }}>
          <button
            type="button"
            className={btn}
            disabled={!canControl || busy || running}
            title={!canControl ? ADMIN_ONLY_TITLE : undefined}
            onClick={() => void onService("start")}
          >
            Start
          </button>
          <button
            type="button"
            className={btn}
            disabled={!canControl || busy || !running}
            title={!canControl ? ADMIN_ONLY_TITLE : undefined}
            onClick={() => void onService("stop")}
          >
            Stop
          </button>
        </div>
      }
    >
      <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
        One QuestDB batch per second into fcr_charger_sample. Stop ends the writer.
      </p>
      <dl className="mt-3 grid gap-1 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
        <div>Last write: {snapshot?.last_ok_at || "—"}</div>
        <div>Chargers in last batch: {snapshot?.chargers ?? "—"}</div>
        <div>Batch time: {typeof snapshot?.batch_s === "number" ? `${snapshot.batch_s}s` : "—"}</div>
        <div className={snapshot?.last_error ? "text-status-error" : ""}>
          Last error: {snapshot?.last_error || "—"}
        </div>
      </dl>
      {error && (
        <p className="mt-2 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
          {error}
        </p>
      )}
      {cmdError && (
        <p className="mt-2 text-status-error" style={{ fontSize: "var(--font-body)" }}>
          {cmdError}
        </p>
      )}
    </FoldCard>
  );
}

export function FcrCoreView() {
  const { user } = useAuth();
  const canControl = canControlFcrCore(user);
  const { snapshot, error, reload } = useFcrCoreModules();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyService, setBusyService] = useState(false);
  const [cmdError, setCmdError] = useState<string | null>(null);
  const [monitorOn, setMonitorOn] = useState<string[]>([]);
  const service = snapshot?.service;
  const alive = Boolean(service?.running);
  const loopPaused = alive && snapshot?.loop === "paused";
  const processRunning = alive && !loopPaused;

  async function onService(action: FcrCoreServiceAction) {
    setBusyService(true);
    setCmdError(null);
    try {
      await postFcrCoreService(action);
      await new Promise((r) => window.setTimeout(r, 1500));
      await reload();
    } catch (err) {
      setCmdError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyService(false);
    }
  }

  async function onCmd(id: string, action: FcrCoreAction) {
    setBusyId(id);
    setCmdError(null);
    try {
      await postFcrCoreCmd(id, action);
      await reload();
    } catch (err) {
      setCmdError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function toggleMonitor(id: string) {
    setMonitorOn((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const modules = snapshot?.modules ?? [];
  const monitorHint =
    monitorOn.length === 0
      ? "still"
      : monitorOn.includes(MONITOR_CORE_ID)
        ? "all"
        : modules
            .filter((m) => monitorOn.includes(m.id))
            .map((m) => m.label)
            .join(" · ");

  return (
    <div className="flex flex-col gap-4">
      <AllocationAdminCard canControl={canControl} />
      <SampleLogCard canControl={canControl} />
      <QuestDbViewCard canControl={canControl} />
      <CardFrame className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
              fcr-core
            </p>
            <p className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
              {processRunning
                ? `running · pid ${service?.pid ?? "—"} · pool ${snapshot?.pool ?? "—"}`
                : loopPaused
                  ? `paused · pid ${service?.pid ?? "—"} · pool ${snapshot?.pool ?? "—"}`
                  : service?.state === "not_loaded"
                    ? "service not installed"
                    : `stopped${service?.last_exit && service.last_exit !== "(never exited)" ? ` · last exit ${service.last_exit}` : ""}`}
              {service?.error ? ` · ${service.error}` : ""}
              {snapshot?.ts ? ` · ${snapshot.ts}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-1" style={{ fontSize: "var(--font-micro)" }}>
            <button
              type="button"
              className={btn}
              disabled={!canControl || busyService || processRunning}
              title={!canControl ? ADMIN_ONLY_TITLE : undefined}
              onClick={() => void onService("start")}
            >
              Start
            </button>
            <button
              type="button"
              className={btn}
              disabled={!canControl || busyService || !alive}
              title={!canControl ? ADMIN_ONLY_TITLE : undefined}
              onClick={() => void onService("stop")}
            >
              Stop
            </button>
            <button
              type="button"
              className={btn}
              disabled={!canControl || busyService}
              title={!canControl ? ADMIN_ONLY_TITLE : undefined}
              onClick={() => void onService("reset")}
            >
              Reset
            </button>
            <MonitorToggle
              on={monitorOn.includes(MONITOR_CORE_ID)}
              onClick={() => toggleMonitor(MONITOR_CORE_ID)}
            />
          </div>
        </div>
        <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
          Modules below stay inside this process. Stop pauses the 1 Hz loop (PID stays); Stop again kills it. Start / pause / reset on a row publish{" "}
          <code className="text-text-primary">fcr/core/cmd</code>.
        </p>
        {error && (
          <p className="mt-2 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
            {error}
          </p>
        )}
        {cmdError && (
          <p className="mt-2 text-status-error" style={{ fontSize: "var(--font-body)" }}>
            {cmdError}
          </p>
        )}
      </CardFrame>

      <FcrCoreMonitorWindow sources={monitorOn} sourceHint={monitorHint} />

      {GROUPS.map((group) => {
        const rows = modules.filter((m) => m.group === group.id);
        if (!rows.length && !snapshot) return null;
        return (
          <CardFrame key={group.id} className="overflow-hidden p-4">
            <div className="mb-3">
              <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
                {group.title}
              </p>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                {group.blurb}
              </p>
            </div>
            {rows.length === 0 ? (
              <p className="text-text-tertiary" style={{ fontSize: "var(--font-body)" }}>
                No snapshot yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border-subtle text-text-secondary">
                      <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                        Module
                      </th>
                      <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                        Kind
                      </th>
                      <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                        State
                      </th>
                      <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                        Detail
                      </th>
                      <th className="py-2 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                        Control
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((mod) => (
                      <ModuleRow
                        key={mod.id}
                        mod={mod}
                        busyId={busyId}
                        processRunning={processRunning}
                        monitorOn={monitorOn.includes(mod.id)}
                        canControl={canControl}
                        onCmd={onCmd}
                        onToggleMonitor={toggleMonitor}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardFrame>
        );
      })}
    </div>
  );
}
